import axios from 'axios';
import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import { format } from 'date-fns';
import { AdminNav } from '../AdminNav';
import { apiUrl } from '../../config';
import {
  AppointmentAvailabilityRuleDto,
  UpsertAppointmentAvailabilityRuleRequest,
} from '../../interfaces/appointments';
import { formatDate } from '../../utils/dateUtils';

const weekdays = ['Søndag', 'Mandag', 'Tirsdag', 'Onsdag', 'Torsdag', 'Fredag', 'Lørdag'];
const weekdayOptions = [1, 2, 3, 4, 5];

type RuleForm = {
  id: number | null;
  name: string;
  startDate: string;
  useEndDate: boolean;
  endDate: string;
  weekdays: number[];
  startTime: string;
  endTime: string;
  allow15: boolean;
  allow30: boolean;
  allow60: boolean;
  isActive: boolean;
};

type GroupedRule = {
  id: number;
  sourceIds: number[];
  ruleGroupKey: string;
  name: string;
  startDate: string;
  endDate?: string;
  weekdays: number[];
  startTime: string;
  endTime: string;
  allow15: boolean;
  allow30: boolean;
  allow60: boolean;
  isActive: boolean;
};

const resolveDefaultWeekday = (): number => {
  const currentDay = new Date().getDay();
  return weekdayOptions.includes(currentDay) ? currentDay : 1;
};

const createDefaultForm = (): RuleForm => ({
  id: null,
  name: 'Dekkskift',
  startDate: new Date().toISOString().slice(0, 10),
  useEndDate: false,
  endDate: '',
  weekdays: [resolveDefaultWeekday()],
  startTime: '09:00',
  endTime: '16:00',
  allow15: true,
  allow30: true,
  allow60: false,
  isActive: true,
});

const sortedWeekdaysLabel = (selectedWeekdays: number[]): string =>
  selectedWeekdays
    .slice()
    .sort((a, b) => a - b)
    .map((weekday) => weekdays[weekday] ?? String(weekday))
    .join(', ');

function AppointmentConfiguration() {
  const [rules, setRules] = useState<AppointmentAvailabilityRuleDto[]>([]);
  const [form, setForm] = useState<RuleForm>(createDefaultForm());
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const groupedRules = useMemo(() => {
    const grouped = new Map<string, GroupedRule>();

    for (const rule of rules) {
      const groupKey = rule.ruleGroupKey || `legacy-${rule.id}`;
      const existing = grouped.get(groupKey);

      if (!existing) {
        grouped.set(groupKey, {
          id: rule.id,
          sourceIds: [rule.id],
          ruleGroupKey: groupKey,
          name: rule.name,
          startDate: rule.startDate,
          endDate: rule.endDate,
          weekdays: [rule.weekday],
          startTime: rule.startTime,
          endTime: rule.endTime,
          allow15: rule.allow15,
          allow30: rule.allow30,
          allow60: rule.allow60,
          isActive: rule.isActive,
        });
        continue;
      }

      existing.sourceIds.push(rule.id);
      existing.weekdays.push(rule.weekday);
    }

    return Array.from(grouped.values())
      .map((rule) => ({
        ...rule,
        sourceIds: Array.from(new Set(rule.sourceIds)),
        weekdays: Array.from(new Set(rule.weekdays)).sort((a, b) => a - b),
      }))
      .sort(
        (a, b) =>
          a.name.localeCompare(b.name, 'nb-NO') ||
          a.startDate.localeCompare(b.startDate) ||
          a.startTime.localeCompare(b.startTime),
      );
  }, [rules]);

  const parseTimeToDate = (timeValue: string): Date => {
    const [hours, minutes] = timeValue.split(':').map((value) => Number(value));
    const result = new Date();
    result.setHours(hours || 0, minutes || 0, 0, 0);
    return result;
  };

  const toIsoDate = (date: Date | null): string => {
    if (!date) {
      return '';
    }

    return format(date, 'yyyy-MM-dd');
  };

  const loadRules = async () => {
    try {
      const response = await axios.get<AppointmentAvailabilityRuleDto[]>(`${apiUrl}/admin/appointments/rules`);
      setRules(response.data);
    } catch {
      setError('Kunne ikke hente oppsett for timebestilling.');
    }
  };

  useEffect(() => {
    loadRules();
  }, []);

  const resetForm = () => {
    setForm(createDefaultForm());
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setSuccess(null);

    const ruleName = form.name.trim();
    if (!ruleName) {
      setError('Regelnavn er påkrevd.');
      setSaving(false);
      return;
    }

    if (!form.allow15 && !form.allow30 && !form.allow60) {
      setError('Velg minst én varighet.');
      setSaving(false);
      return;
    }

    const selectedWeekdays = Array.from(new Set(form.weekdays)).filter((weekday) => weekdayOptions.includes(weekday));
    if (selectedWeekdays.length === 0) {
      setError('Velg minst én ukedag (mandag-fredag).');
      setSaving(false);
      return;
    }

    const payload: UpsertAppointmentAvailabilityRuleRequest = {
      name: ruleName,
      startDate: form.startDate,
      endDate: form.useEndDate ? form.endDate : undefined,
      weekdays: selectedWeekdays,
      startTime: form.startTime,
      endTime: form.endTime,
      allow15: form.allow15,
      allow30: form.allow30,
      allow60: form.allow60,
      isActive: form.isActive,
    };

    try {
      if (form.id) {
        await axios.put(`${apiUrl}/admin/appointments/rules/${form.id}`, payload);
      } else {
        await axios.post(`${apiUrl}/admin/appointments/rules`, payload);
      }

      setSuccess('Oppsett lagret.');
      resetForm();
      await loadRules();
    } catch (err: any) {
      if (err?.response?.data) {
        setError(String(err.response.data));
      } else {
        setError('Kunne ikke lagre oppsettet.');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (rule: GroupedRule) => {
    setError(null);
    setSuccess(null);

    try {
      await axios.delete(`${apiUrl}/admin/appointments/rules/${rule.id}`);
      setSuccess('Regel slettet.');
      await loadRules();
      if (form.id && rule.sourceIds.includes(form.id)) {
        resetForm();
      }
    } catch {
      setError('Kunne ikke slette regelen.');
    }
  };

  const handleEdit = (rule: GroupedRule) => {
    setForm({
      id: rule.id,
      name: rule.name,
      startDate: rule.startDate,
      useEndDate: !!rule.endDate,
      endDate: rule.endDate ?? '',
      weekdays: rule.weekdays,
      startTime: rule.startTime,
      endTime: rule.endTime,
      allow15: rule.allow15,
      allow30: rule.allow30,
      allow60: rule.allow60,
      isActive: rule.isActive,
    });
    setError(null);
    setSuccess(null);
  };

  return (
    <Grid container>
      <Grid item xs={2}>
        <AdminNav />
      </Grid>
      <Grid item xs={10} sx={{ p: 3 }}>
        <Typography variant="h5" color="text.secondary" gutterBottom>
          Timebestilling oppsett
        </Typography>

        <Paper sx={{ p: 3, mb: 3 }}>
          <Stack spacing={2}>
            <Typography variant="h6">{form.id ? 'Rediger regel' : 'Ny regel'}</Typography>

            <TextField
              label="Regelnavn"
              value={form.name}
              onChange={(event) => setForm((prev) => ({ ...prev, name: event.target.value }))}
              placeholder="F.eks. Dekkskift"
              fullWidth
            />

            <Stack direction="row" spacing={2}>
              <DatePicker
                label="Fra dato"
                inputFormat="dd/MM/yyyy"
                value={form.startDate ? new Date(`${form.startDate}T00:00:00`) : null}
                onChange={(newValue) => setForm((prev) => ({ ...prev, startDate: toIsoDate(newValue) }))}
                renderInput={(params) => <TextField {...params} fullWidth />}
              />
              <DatePicker
                label="Til dato"
                inputFormat="dd/MM/yyyy"
                disabled={!form.useEndDate}
                value={form.endDate ? new Date(`${form.endDate}T00:00:00`) : null}
                onChange={(newValue) => setForm((prev) => ({ ...prev, endDate: toIsoDate(newValue) }))}
                renderInput={(params) => <TextField {...params} fullWidth />}
              />
            </Stack>

            <FormControlLabel
              control={
                <Switch
                  checked={form.useEndDate}
                  onChange={(event) =>
                    setForm((prev) => ({
                      ...prev,
                      useEndDate: event.target.checked,
                      endDate: event.target.checked ? prev.endDate || prev.startDate : '',
                    }))
                  }
                />
              }
              label="Bruk til-dato"
            />

            <Stack direction="row" spacing={2}>
              <FormControl fullWidth>
                <InputLabel id="weekday-label">Ukedager</InputLabel>
                <Select
                  labelId="weekday-label"
                  multiple
                  label="Ukedager"
                  value={form.weekdays.map((value) => String(value))}
                  onChange={(event: SelectChangeEvent<string[]>) => {
                    const selectedValues =
                      typeof event.target.value === 'string'
                        ? event.target.value.split(',')
                        : event.target.value;

                    setForm((prev) => ({
                      ...prev,
                      weekdays: selectedValues.map((value) => Number(value)),
                    }));
                  }}
                  renderValue={(selected) => sortedWeekdaysLabel(selected.map((value) => Number(value)))}
                >
                  {weekdayOptions.map((index) => (
                    <MenuItem key={index} value={String(index)}>
                      {weekdays[index]}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <TimePicker
                ampm={false}
                label="Fra tid"
                value={parseTimeToDate(form.startTime)}
                onChange={(newValue) => {
                  if (newValue) {
                    setForm((prev) => ({ ...prev, startTime: format(newValue, 'HH:mm') }));
                  }
                }}
                renderInput={(params) => <TextField {...params} fullWidth />}
              />

              <TimePicker
                ampm={false}
                label="Til tid"
                value={parseTimeToDate(form.endTime)}
                onChange={(newValue) => {
                  if (newValue) {
                    setForm((prev) => ({ ...prev, endTime: format(newValue, 'HH:mm') }));
                  }
                }}
                renderInput={(params) => <TextField {...params} fullWidth />}
              />
            </Stack>

            <Stack direction="row" spacing={2}>
              <FormControlLabel
                control={<Switch checked={form.allow15} onChange={(event) => setForm((prev) => ({ ...prev, allow15: event.target.checked }))} />}
                label="15 min"
              />
              <FormControlLabel
                control={<Switch checked={form.allow30} onChange={(event) => setForm((prev) => ({ ...prev, allow30: event.target.checked }))} />}
                label="30 min"
              />
              <FormControlLabel
                control={<Switch checked={form.allow60} onChange={(event) => setForm((prev) => ({ ...prev, allow60: event.target.checked }))} />}
                label="60 min"
              />
              <FormControlLabel
                control={<Switch checked={form.isActive} onChange={(event) => setForm((prev) => ({ ...prev, isActive: event.target.checked }))} />}
                label="Aktiv"
              />
            </Stack>

            {error && <Alert severity="error">{error}</Alert>}
            {success && <Alert severity="success">{success}</Alert>}

            <Stack direction="row" spacing={2}>
              <Button variant="contained" onClick={handleSave} disabled={saving}>
                {saving ? 'Lagrer...' : form.id ? 'Oppdater regel' : 'Lagre regel'}
              </Button>
              {form.id && (
                <Button variant="outlined" color="inherit" onClick={resetForm}>
                  Avbryt redigering
                </Button>
              )}
            </Stack>
          </Stack>
        </Paper>

        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Regler
          </Typography>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Regelnavn</TableCell>
                <TableCell>Ukedager</TableCell>
                <TableCell>Fra</TableCell>
                <TableCell>Til</TableCell>
                <TableCell>Tid</TableCell>
                <TableCell>Varigheter</TableCell>
                <TableCell>Aktiv</TableCell>
                <TableCell align="right">Handlinger</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {groupedRules.map((rule) => (
                <TableRow key={rule.ruleGroupKey}>
                  <TableCell>{rule.name}</TableCell>
                  <TableCell>{sortedWeekdaysLabel(rule.weekdays)}</TableCell>
                  <TableCell>{formatDate(rule.startDate, 'dd/MM/yyyy')}</TableCell>
                  <TableCell>{rule.endDate ? formatDate(rule.endDate, 'dd/MM/yyyy') : 'Åpen'}</TableCell>
                  <TableCell>{rule.startTime} - {rule.endTime}</TableCell>
                  <TableCell>
                    {[rule.allow15 ? '15' : null, rule.allow30 ? '30' : null, rule.allow60 ? '60' : null].filter(Boolean).join(', ')}
                  </TableCell>
                  <TableCell>{rule.isActive ? 'Ja' : 'Nei'}</TableCell>
                  <TableCell align="right">
                    <Stack direction="row" spacing={1} justifyContent="flex-end">
                      <Button size="small" onClick={() => handleEdit(rule)}>Rediger</Button>
                      <Button size="small" color="error" onClick={() => handleDelete(rule)}>Slett</Button>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      </Grid>
    </Grid>
  );
}

export default AppointmentConfiguration;
